import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { Menu, Mutation, MutationUpdateSubscriptionBusyModeArgs as MutationVariables } from 'generated/graphql';
import { AppsList } from 'components/AppsList/AppsList';
import { SelectMenu } from 'components/SelectMenu';
import AvailabilityPill from 'components/AvailabilityPill/AvailabilityPill';
import SidePanel from 'components/SidePanel/SidePanel';
import DetailsFooter from 'components/SingleItem/DetailsFooter';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { UPDATE_BUSY_MODE_MUTATION } from 'queries/Venue';
import { useModalContext } from 'common/Context/ModalContext';
import { MenuFragment } from 'queries/Menu';
import { SnackBarTypeEnum } from 'types';
import { getEnvironmentVariables } from 'services/environment-variables/environment-variables';
import { AmplitudeEventCategories, AmplitudeEventProperties, logAmplitudeEvent } from 'utils/Amplitude';
import { convertBooleanToAvailability } from 'utils';
import { groupByBrand, type TileSection } from 'components/Tiles/tileUtils';
import styles from './BusyMode.module.scss';

interface Props {
  menus?: Menu[] | null;
  venueId?: string;
  venueName?: string;
}

type MutationData = { updateSubscriptionBusyMode: Mutation['updateSubscriptionBusyMode'] };

const VIRTUAL_BRAND_IDENTIFIER = 'virtual';
// Values are expressed in minutes
const BUSY_DURATION_OPTIONS = [0, 15, 30, 45, 60, 90, 120, 240, 480, 720, 1440];
const MILLISECONDS_IN_ONE_MINUTE = 60000;

enum Align {
  Left = 'left',
  Right = 'right',
  Center = 'center'
}

enum Size {
  Small = 'small',
  Large = 'large'
}

enum TextAlignment {
  Left = 'left',
  Center = 'center',
  Right = 'right'
}

const selectMenuProps = {
  buttonStyle: styles.Button,
  align: Align.Right,
  size: Size.Small,
  height: 263,
  width: 258,
  textAlignment: TextAlignment.Left
};

const getLabelForValue = (value: number): string => {
  if (value === 0) {
    return 'Available';
  }

  if (value > 90) {
    const hours = value / 60;
    return `Unavailable for ${hours} hours`;
  }

  return `Unavailable for ${value} mins`;
};

const BusyMode: React.FC<Props> = ({ menus, venueId, venueName }) => {
  const [originalBusyUntilValue, setOriginalBusyUntilValue] = useState<{ [key: string]: number }>({});
  const [busyUntil, setBusyUntil] = useState<{ [key: string]: number }>({});
  const [loading, setLoading] = useState(false);
  const [updatedAppNames, setUpdatedAppNames] = useState<string[]>([]);
  const location = useLocation();
  const { openSnackBar } = useModalContext();
  const navigate = useNavigate();
  const [updateSubscriptionMutation] = useMutation<MutationData, MutationVariables>(UPDATE_BUSY_MODE_MUTATION, {
    optimisticResponse: (variables) => ({
      updateSubscriptionBusyMode: variables.busyUntil
    }),
    update: (cache, { data }, { variables }) => {
      if (!data) {
        throw new Error(`Expected mutation callback data but got undefined`);
      }

      if (!variables) {
        throw new Error(`Expected mutation callback variables but got undefined`);
      }

      const nextBusyUntil = data.updateSubscriptionBusyMode;

      const menu = availableMenus.find((item) => {
        if (!variables.brandId) {
          return item.apps[0].id === variables.appId;
        }

        return variables.appId === item.apps[0].id.split('.')[1];
      });
      if (!menu) {
        throw new Error(`Expected to find menu with app ID ${variables.appId} in availableMenus`);
      }

      const id = cache.identify(menu);

      cache.updateFragment(
        {
          fragmentName: 'MenuFragment',
          id,
          fragment: MenuFragment
        },
        (data) => ({ ...data, busyUntil: nextBusyUntil })
      );
    }
  });

  const { BUSY_MODE_WHITELISTED_APP_IDS } = getEnvironmentVariables();

  // Filter whitelisted apps
  const availableMenus: Menu[] = useMemo(() => {
    if (!menus || !BUSY_MODE_WHITELISTED_APP_IDS) {
      return [];
    }

    return menus.filter((menu) => {
      if (menu.neverPublished) {
        return false;
      }

      const app = menu.apps[0];

      if (BUSY_MODE_WHITELISTED_APP_IDS.includes(Number(app.id))) {
        return true;
      }

      return BUSY_MODE_WHITELISTED_APP_IDS.includes(Number(app.id.split('.')[1]));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menus]);

  const nonSupportedAppMenus = menus?.filter(
    (menu) =>
      !(
        BUSY_MODE_WHITELISTED_APP_IDS?.includes(Number(menu.apps[0].id)) ||
        BUSY_MODE_WHITELISTED_APP_IDS?.includes(Number(menu.apps[0].id.split('.')[1]))
      )
  );

  const groupedMenus: TileSection[] = useMemo(() => {
    if (!availableMenus) {
      return [];
    }
    return groupByBrand(availableMenus, venueName ?? 'Menu');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableMenus]);

  const closeModal = (): void => {
    const { pathname } = location;
    const pathnameWithoutHash = pathname.split(`/busy`)[0];

    navigate(pathnameWithoutHash);
  };

  const handleSelectChange = (appId: string, value: number): void => {
    setBusyUntil((prevBusyUntil) => {
      const newBusyUntil = {
        ...prevBusyUntil,
        [appId]: value
      };
      return newBusyUntil;
    });
  };

  // Set the original busy until values to the closest option from the dropdowns if a menu is currently paused
  const setOriginalBusyUntilValues = useCallback(() => {
    let newBusyUntil: { [key: string]: number } = {};
    availableMenus.forEach((menu) => {
      if (menu.busyUntil && new Date(menu.busyUntil) > new Date()) {
        const dateFromNow = new Date(menu.busyUntil).getTime() - new Date().getTime();
        const minutes = Math.floor(dateFromNow / MILLISECONDS_IN_ONE_MINUTE);
        const closestOption = BUSY_DURATION_OPTIONS.reduce((prev, curr) =>
          Math.abs(Number(curr) - minutes) < Math.abs(Number(prev) - minutes) ? curr : prev
        );

        newBusyUntil = {
          ...newBusyUntil,
          [menu.apps[0].id]: closestOption === 0 ? 15 : closestOption
        };
        return newBusyUntil;
      }
      newBusyUntil = {
        ...newBusyUntil,
        [menu.apps[0].id]: 0
      };
      return newBusyUntil;
    });
    setOriginalBusyUntilValue(newBusyUntil);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableMenus]);

  useEffect(setOriginalBusyUntilValues, [setOriginalBusyUntilValues]);

  const displaySnackBar = (message: string, type: SnackBarTypeEnum): void => {
    openSnackBar(message, undefined, undefined, type, 3000);
  };

  // Update the subscription with the new busy until value and update the cache
  const updateSubscriptionWithBusyUntilValue = async (appId: string, busyUntil: number): Promise<void> => {
    const currentDate = new Date();
    const busyUntilDate =
      busyUntil !== 0 ? new Date(currentDate.getTime() + Number(busyUntil) * MILLISECONDS_IN_ONE_MINUTE) : null;
    const isBrand = appId.includes(VIRTUAL_BRAND_IDENTIFIER);
    const [, brandAppId, brandId] = appId.split('.');
    try {
      if (!venueId) {
        throw new Error(`Expected to have BusyMode prop venueId`);
      }

      await updateSubscriptionMutation({
        variables: {
          locationId: venueId,
          appId: isBrand ? brandAppId : appId,
          busyUntil: busyUntilDate?.toISOString() || null,
          ...(isBrand && { brandId })
        }
      });
    } catch (error) {
      throw new Error(`Failed to update subscription for appId: ${appId}`);
    }
  };

  const handleConfirm = async (): Promise<void> => {
    if (Object.entries(busyUntil).length === 0) {
      setLoading(false);
      return;
    }
    const updateMenuPromises: Promise<void>[] = [];
    Object.entries(busyUntil).forEach(([appId, busyUntilValue]) => {
      updateMenuPromises.push(updateSubscriptionWithBusyUntilValue(appId, busyUntilValue));
    });

    if (updateMenuPromises.length === 0) {
      setLoading(false);
      return;
    }

    try {
      await Promise.allSettled(updateMenuPromises);
      displaySnackBar('Your selected app menus have been updated.', SnackBarTypeEnum.SUCCESS);
    } catch (error) {
      displaySnackBar('An error occurred while updating your menus. Please try again later.', SnackBarTypeEnum.INFO);
    } finally {
      setLoading(false);
      closeModal();
    }
  };

  const renderAppsSection = (menu: TileSection): React.ReactNode => {
    const apps = menu.menus.flatMap((menu) => menu.apps);
    return (
      <div>
        {availableMenus.find((menu) => menu.apps[0].id.includes(VIRTUAL_BRAND_IDENTIFIER)) && (
          <div className={styles.TopSection}>
            <span>{menu.name}</span>
          </div>
        )}
        <div className={styles.Section}>
          <ul className={styles.OptionList} data-testid="option-list">
            <li className={styles.HeadingRow}>
              {apps && <AppsList apps={apps} />}
              <span className={styles.AllApps}>All apps</span>
              <div className={styles.SelectMenu}>
                <SelectMenu
                  options={BUSY_DURATION_OPTIONS.map((option) => {
                    const value = getLabelForValue(option);
                    return {
                      content: <span>{value}</span>,
                      value,
                      type: 'button'
                    };
                  })}
                  defaultButtonContent={
                    <AvailabilityPill
                      availability={
                        busyUntil[apps[0].id] !== undefined
                          ? getLabelForValue(busyUntil[apps[0].id])
                          : getLabelForValue(originalBusyUntilValue[apps[0].id])
                      }
                    />
                  }
                  downIcon={null}
                  onChange={(value) => {
                    const selectedOption = BUSY_DURATION_OPTIONS.find((option) => getLabelForValue(option) === value);
                    if (selectedOption !== undefined) {
                      apps?.forEach((app) => handleSelectChange(app.id, selectedOption));
                      const appNames = apps.map((app) =>
                        app.id.includes('virtual') && app.brand ? `${app.name} - brand` : app.name
                      );
                      setUpdatedAppNames([
                        ...updatedAppNames,
                        ...appNames.filter((appName) => !updatedAppNames.includes(appName))
                      ]);
                      logAmplitudeEvent({
                        name: `mark ${convertBooleanToAvailability(selectedOption === 0)} for all apps`,
                        category: AmplitudeEventCategories.MM_SIDE_PANEL_BUSY_MODE,
                        properties: {
                          [AmplitudeEventProperties.APPS]: appNames.join(', '),
                          ...(selectedOption !== 0 && {
                            [AmplitudeEventProperties.INCREMENT]: `${getLabelForValue(selectedOption).split('for ')[1]}`
                          })
                        }
                      });
                    }
                  }}
                  {...selectMenuProps}
                />
              </div>
            </li>
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.BusyMode}>
      <SidePanel
        title="Busy mode"
        description="Feel in control during the rush and pause your menus on the apps for as long as you need."
        route="busy"
      >
        <div className={styles.Container}>
          <div className={styles.PauseAppsSection}>
            {groupedMenus.map((menu) => renderAppsSection(menu))}
            {nonSupportedAppMenus?.length ? (
              <div className={styles.UnsupportedApps} data-testid="unsupported-apps">
                {nonSupportedAppMenus.map(({ apps }) => (
                  <AppsList apps={apps} key={apps[0].id} />
                ))}
                <span className={styles.NotSupported}>
                  <i className="mm-error-circle" /> App{nonSupportedAppMenus.length > 1 ? `s don't` : ` doesn't`}{' '}
                  support busy mode
                </span>
              </div>
            ) : null}
          </div>
        </div>

        <div className={styles.Footer}>
          <DetailsFooter
            closeModal={() => {
              closeModal();
              logAmplitudeEvent({
                name: 'click cancel',
                category: AmplitudeEventCategories.MM_SIDE_PANEL_BUSY_MODE
              });
            }}
            handleConfirm={() => {
              setLoading(true);
              handleConfirm();
              logAmplitudeEvent({
                name: 'click update',
                category: AmplitudeEventCategories.MM_SIDE_PANEL_BUSY_MODE,
                properties: {
                  enabled: `${Object.values(busyUntil).some((value) => value !== 0)}`,
                  ...(updatedAppNames.length && {
                    apps: `${updatedAppNames.join(', ')}`
                  })
                }
              });
            }}
            confirmButton="Update"
            loading={loading}
          />
        </div>
      </SidePanel>
    </div>
  );
};

export default BusyMode;
